import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, CheckCircle2, ArrowRight, BookOpen, Mail } from "lucide-react";
import { fetchAdminSubmissions } from "@/api/adminSubmissions";
import { fetchContactMessages } from "@/api/adminContact";
import { adminApi } from "@/api/adminClient";

const StatCard = ({ icon: Icon, label, value, color, to }) => (
  <Link
    to={to}
    className="block bg-white rounded-2xl border border-[#E4EEF5] p-5 hover:shadow-md transition"
  >
    <div className="flex items-center gap-3 mb-3">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: `${color}20`, color: color }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-sm font-[Poppins] font-semibold text-[#5A7594]">
        {label}
      </p>
    </div>
    <p className="text-4xl font-[Poppins] font-extrabold text-[#173A5E]">
      {value}
    </p>
  </Link>
);

export const AdminDashboardPage = () => {
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [listings, setListings] = useState(0);
  const [contactNew, setContactNew] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, a, r, facets, cNew] = await Promise.all([
          fetchAdminSubmissions("pending"),
          fetchAdminSubmissions("approved"),
          fetchAdminSubmissions("rejected"),
          adminApi.get("/listings/facets"),
          fetchContactMessages("new"),
        ]);
        if (cancelled) return;
        setCounts({ pending: p.length, approved: a.length, rejected: r.length });
        setListings(facets.data.total || 0);
        setContactNew(cNew.length);
      } catch {
        /* silent */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <p className="text-xs font-[Poppins] font-semibold uppercase tracking-wider text-[#2E9CD6] mb-1">
        Beheer
      </p>
      <h1 className="text-3xl sm:text-4xl font-bold">Dashboard</h1>
      <p className="mt-2 text-[#3E5871] font-[Inter] max-w-2xl mb-8">
        Overzicht van de gids en de review-wachtrij.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={Inbox}
          label="In behandeling"
          value={loading ? "…" : counts.pending}
          color="#F3C300"
          to="/beheer/inzendingen"
        />
        <StatCard
          icon={Mail}
          label="Nieuwe contactberichten"
          value={loading ? "…" : contactNew}
          color="#CE1B2A"
          to="/beheer/contact"
        />
        <StatCard
          icon={BookOpen}
          label="Bedrijven live"
          value={loading ? "…" : listings}
          color="#2E9CD6"
          to="/bedrijvengids"
        />
        <StatCard
          icon={CheckCircle2}
          label="Goedgekeurd"
          value={loading ? "…" : counts.approved}
          color="#22a56b"
          to="/beheer/inzendingen"
        />
      </div>

      <div className="bg-white rounded-2xl border border-[#E4EEF5] p-6 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
          <h2 className="text-xl font-[Poppins] font-bold text-[#173A5E]">
            Wat kun je hier doen?
          </h2>
          <Link
            to="/beheer/inzendingen"
            data-testid="dashboard-to-submissions"
            className="inline-flex items-center gap-1 text-sm text-[#2E9CD6] font-[Poppins] font-semibold hover:text-[#173A5E]"
          >
            Naar de wachtrij
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <ul className="text-[#3E5871] font-[Inter] leading-relaxed space-y-1 list-disc pl-5">
          <li>Nieuwe aanmeldingen goedkeuren — er wordt automatisch een listing aangemaakt.</li>
          <li>Aanvullingen op bestaande bedrijven doorvoeren — je ziet vóór goedkeuring welke velden wijzigen.</li>
          <li>Inzendingen afwijzen met een reden, zonder de live gids te wijzigen.</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
